import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, RefreshCw } from 'lucide-react';

const statuses = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'] as const;
type TicketStatus = typeof statuses[number];
const labels: Record<TicketStatus, string> = {
  open: 'Ontvangen', in_progress: 'In behandeling', waiting_customer: 'Wacht op klant',
  resolved: 'Opgelost', closed: 'Gesloten',
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ServiceIntakeRow {
  request_id: string;
  ticket_id: string;
  ticket_number: string;
  site_name: string;
  asset_name: string | null;
  contact_name: string | null;
  title: string;
  description: string;
  crm_status: string;
  routing_state: string;
  attempts: number;
  created_at: string;
}
export interface ServiceIntakePort {
  list(): Promise<ServiceIntakeRow[]>;
  updateStatus(ticketId: string, status: TicketStatus): Promise<void>;
}

function parseRow(value: unknown): ServiceIntakeRow {
  if (!value || typeof value !== 'object') throw new Error('Unexpected queue response');
  const row = value as Record<string, unknown>;
  for (const field of ['ticket_number', 'site_name', 'title', 'description', 'crm_status', 'routing_state', 'created_at']) {
    if (typeof row[field] !== 'string') throw new Error('Unexpected queue response');
  }
  if (typeof row.request_id !== 'string' || !UUID.test(row.request_id) ||
    typeof row.ticket_id !== 'string' || !UUID.test(row.ticket_id) ||
    typeof row.attempts !== 'number' || !Number.isInteger(row.attempts) || row.attempts < 0 ||
    !Number.isFinite(Date.parse(String(row.created_at))) ||
    (row.asset_name !== null && typeof row.asset_name !== 'string') ||
    (row.contact_name !== null && typeof row.contact_name !== 'string')) {
    throw new Error('Unexpected queue response');
  }
  return row as unknown as ServiceIntakeRow;
}

// This uses the ordinary CRM user's existing session. No server key, raw Auth
// user list or customer permissions are introduced in the browser.
export function createServiceIntakePort(client: SupabaseClient): ServiceIntakePort {
  return {
    async list() {
      const { data, error } = await client.from('client_service_crm_queue')
        .select('request_id,ticket_id,ticket_number,site_name,asset_name,contact_name,title,description,crm_status,routing_state,attempts,created_at')
        .order('created_at', { ascending: false }).limit(100);
      if (error || !Array.isArray(data)) throw new Error('Service queue unavailable');
      return data.map(parseRow);
    },
    async updateStatus(ticketId, status) {
      if (!UUID.test(ticketId) || !statuses.includes(status)) throw new Error('Invalid ticket status');
      const { data, error } = await client.from('tickets').update({ status })
        .eq('id', ticketId).eq('source', 'customer_app').is('deleted_at', null)
        .select('id,status').single();
      if (error || data?.id !== ticketId || data?.status !== status) {
        throw new Error('Ticket status was not confirmed');
      }
    },
  };
}

export function ServiceIntakePanel({ userId, port }: {
  userId: string | null;
  port: ServiceIntakePort;
}) {
  const generation = useRef(0);
  const [snapshot, setSnapshot] = useState<{ userId: string | null; rows: ServiceIntakeRow[] }>({ userId: null, rows: [] });
  const rows = snapshot.userId === userId ? snapshot.rows : [];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const load = useCallback(async (current: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await port.list();
      if (generation.current === current) setSnapshot({ userId, rows: data });
    } catch {
      if (generation.current === current) {
        setSnapshot({ userId, rows: [] });
        setError('Servicemeldingen konden niet worden geladen. Probeer opnieuw.');
      }
    } finally {
      if (generation.current === current) setLoading(false);
    }
  }, [port, userId]);

  useEffect(() => {
    const current = ++generation.current;
    setSnapshot({ userId, rows: [] });
    setSaving(null);
    setError(null);
    if (userId) void load(current);
    else setLoading(false);
    return () => { ++generation.current; };
  }, [userId, load]);

  async function changeStatus(row: ServiceIntakeRow, status: TicketStatus) {
    if (!userId || saving) return;
    const current = generation.current;
    setSaving(row.ticket_id);
    setError(null);
    try {
      await port.updateStatus(row.ticket_id, status);
      if (current === generation.current) await load(current);
    } catch {
      if (current === generation.current) {
        setError('De nieuwe status is niet bevestigd. Vernieuw de lijst voordat je opnieuw probeert.');
      }
    } finally {
      if (current === generation.current) setSaving(null);
    }
  }

  if (!userId) return null;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>Servicemeldingen uit de klantapp</CardTitle>
        <Button variant="outline" size="sm" disabled={loading || !!saving}
          onClick={() => void load(generation.current)}>
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />Vernieuwen
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        {loading && <p role="status" className="text-sm text-muted-foreground">Servicemeldingen laden…</p>}
        {!loading && !error && !rows.length && <p className="text-sm text-muted-foreground">Er zijn nog geen servicemeldingen uit de klantapp.</p>}
        {rows.map(row => (
          <article key={row.request_id} className="space-y-3 rounded-xl border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{row.ticket_number} · {row.site_name}</p>
                <h3 className="mt-1 font-semibold">{row.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {row.contact_name ?? 'Interne pilotgebruiker'}
                  {row.asset_name ? ` · ${row.asset_name}` : ''}
                  {' · '}{new Date(row.created_at).toLocaleString('nl-NL')}
                </p>
              </div>
              <label className="space-y-1 text-sm">
                <span className="block text-muted-foreground">Klantstatus</span>
                <select className="rounded-md border bg-background px-3 py-2"
                  aria-label={`Status van ${row.ticket_number}`} value={row.crm_status}
                  disabled={!!saving || loading}
                  onChange={event => void changeStatus(row, event.target.value as TicketStatus)}>
                  {!statuses.includes(row.crm_status as TicketStatus) && <option value={row.crm_status}>Status onbekend</option>}
                  {statuses.map(status => <option key={status} value={status}>{labels[status]}</option>)}
                </select>
              </label>
            </div>
            <p className="whitespace-pre-wrap break-words text-sm">{row.description}</p>
            {row.routing_state === 'delivered' ? <Badge variant="secondary">CRM-notificatie opgeslagen</Badge> : (
              <p className="flex items-center gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                {row.routing_state === 'blocked_unconfigured'
                  ? 'Er zijn nog geen ontvangers voor deze locatie ingesteld.'
                  : row.routing_state === 'failed'
                    ? 'De CRM-notificatie is nog niet afgeleverd. De melding staat wel in de werkvoorraad.'
                    : 'De melding staat in de werkvoorraad; de CRM-notificatie wordt verwerkt.'}
              </p>
            )}
          </article>
        ))}
      </CardContent>
    </Card>
  );
}
